import { BaseComponent, BElement } from 'utils/base-component'
import './index.scss'

require.context('./static', true, /\.(svg|png)$/)

export class GreenroomArticles extends BaseComponent {
  static cssClass = 'greenroom-articles'

  private carousel!: BElement
  private isDragging = false
  private startX = 0
  private scrollLeft = 0
  private draggingTimeout: number | null = null

  init() {
    this.carousel = new BElement(
      document.querySelector('.greenroom-articles__carousel'),
      this.cssClass
    )

    if (this.carousel && this.carousel.element) {
      // Add mouse event listeners for drag functionality
      this.carousel.element.addEventListener('mousedown', this.startDrag.bind(this))
      this.carousel.element.addEventListener('mouseleave', this.endDrag.bind(this))
      this.carousel.element.addEventListener('mouseup', this.endDrag.bind(this))
      this.carousel.element.addEventListener('mousemove', this.drag.bind(this))

      // Add cursor styles to indicate draggable content
      this.carousel.element.style.cursor = 'grab'
    }
  }

  private startDrag(e: MouseEvent) {
    if (!this.carousel.element) return

    // Get the clicked element and check if it's a scroll button or within its area
    const clickX = e.clientX
    const carouselRect = this.carousel.element.getBoundingClientRect()
    const buttonWidth = 60 // Approximate width of the scroll button area

    // Check if click is in left or right button area
    if (clickX - carouselRect.left < buttonWidth || carouselRect.right - clickX < buttonWidth) {
      return // Click is in button area, don't initiate drag
    }

    if (this.draggingTimeout) {
      window.clearTimeout(this.draggingTimeout)
      this.draggingTimeout = null
    }

    this.isDragging = true
    this.startX = e.pageX - this.carousel.element.offsetLeft
    this.scrollLeft = this.carousel.element.scrollLeft

    // Add dragging class
    // Temporarily removes smooth scrolling during drag for responsiveness
    this.carousel.element.classList.add('dragging')
    this.carousel.element.style.cursor = 'grabbing'
    e.preventDefault()
  }

  private endDrag() {
    if (!this.carousel.element || !this.isDragging) return

    this.isDragging = false
    this.carousel.element.style.cursor = 'grab'

    // Calculate target position for smooth snapping
    this.calculateSnapPosition()

    // Remove dragging class after a small delay to allow for smooth transition
    this.draggingTimeout = window.setTimeout(() => {
      if (this.carousel.element) {
        this.carousel.element.classList.remove('dragging')
      }
      this.draggingTimeout = null
    }, 50) // Short delay before re-enabling snap
  }

  private calculateSnapPosition() {
    if (!this.carousel.element) return

    const currentScroll = this.carousel.element.scrollLeft

    // Get slide width (including gap)
    const slides = this.carousel.element.querySelectorAll('.greenroom-articles__carousel__slide')
    if (!slides.length) return

    const slideRect = slides[0].getBoundingClientRect()
    const slideWidth = slideRect.width + 20 // Add gap (1.25rem ≈ 20px)

    // Calculate which slide we should snap to
    const slideIndex = Math.round(currentScroll / slideWidth)
    const targetPosition = slideIndex * slideWidth

    // Add smooth scrolling class
    this.carousel.element.classList.add('smooth-return')

    // Scroll to the target position
    this.carousel.element.scrollLeft = targetPosition

    // Remove smooth class after transition completes
    window.setTimeout(() => {
      if (this.carousel.element) {
        this.carousel.element.classList.remove('smooth-return')
      }
    }, 300) // Match this with the transition duration in CSS
  }

  private drag(e: MouseEvent) {
    if (!this.isDragging || !this.carousel.element) return

    const x = e.pageX - this.carousel.element.offsetLeft
    const walk = (x - this.startX) * 1.5 // Scroll multiplier - adjust for faster/slower drag
    this.carousel.element.scrollLeft = this.scrollLeft - walk

    // Prevent default behaviors while dragging
    e.preventDefault()
  }
}
